import React, { useState } from 'react';
import {
  AppBar, Toolbar, IconButton, Typography, Box, Drawer,
  List, ListItemButton, ListItemIcon, ListItemText, Divider,
  useMediaQuery, Tooltip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LogoutIcon from '@mui/icons-material/Logout';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../provider/authProvider';
import { useColorMode } from '../provider/colorModeProvider';

const DRAWER_WIDTH = 240;

/**
 * Responsive app shell.
 *   - Desktop (md+): persistent left drawer + top AppBar.
 *   - Mobile  (xs..sm): temporary drawer toggled by hamburger.
 * Every page (admin or client) renders inside the shell's <main>.
 */
export default function AppShell({
  title = 'Peak Portal',
  brand = 'Peak Software',
  menuItems = [], // [{key, label, icon, onClick, active}]
  children,
}) {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const { setToken } = useAuth();
  const { mode, toggle: toggleMode } = useColorMode();

  const handleLogout = () => {
    setToken(null);
    sessionStorage.removeItem('companyInfo');
    sessionStorage.removeItem('adminApiToken');
    navigate('/login', { replace: true });
  };

  const drawerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box
        sx={{
          px: 2, py: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          minHeight: 80,
        }}
      >
        <Box
          component="img"
          src="/img/Peak_Logo_Kobalt.png"
          alt="Peak"
          sx={{ width: 56, height: 56, objectFit: 'contain', flexShrink: 0 }}
        />
        <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>{brand}</Typography>
      </Box>
      <Divider />
      <List sx={{ flex: 1, py: 1 }}>
        {menuItems.map((m) => (
          <ListItemButton
            key={m.key}
            selected={m.active}
            onClick={() => { m.onClick(); if (!isDesktop) setMobileOpen(false); }}
            sx={{
              mx: 1,
              borderRadius: 2,
              mb: 0.5,
              '&.Mui-selected': {
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                '& .MuiListItemIcon-root': { color: 'inherit' },
                '&:hover': { bgcolor: 'primary.dark' },
              },
            }}
          >
            {m.icon && <ListItemIcon sx={{ minWidth: 36 }}>{m.icon}</ListItemIcon>}
            <ListItemText primary={m.label} primaryTypographyProps={{ fontWeight: 600 }} />
          </ListItemButton>
        ))}
      </List>
      <Divider />
      <Box sx={{ p: 2, fontSize: 12, color: 'text.secondary', textAlign: 'center' }}>
        © {new Date().getFullYear()} Peak Software
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default', overflowX: 'hidden' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          ml: { md: `${DRAWER_WIDTH}px` },
          // iOS PWA status bar (black-translucent) draws over the AppBar.
          // env() padding pushes our content below the system UI; the AppBar
          // background extends behind the status bar for an edge-to-edge feel.
          pt: 'env(safe-area-inset-top)',
          pl: 'env(safe-area-inset-left)',
          pr: 'env(safe-area-inset-right)',
        }}
      >
        <Toolbar sx={{ gap: 1 }}>
          {!isDesktop && (
            <IconButton edge="start" onClick={() => setMobileOpen(true)} sx={{ mr: 1 }}>
              <MenuIcon />
            </IconButton>
          )}
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 700 }}>{title}</Typography>
          <Tooltip title={mode === 'dark' ? 'Açık tema' : 'Koyu tema'}>
            <IconButton onClick={toggleMode}>
              {mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Çıkış">
            <IconButton onClick={handleLogout}>
              <LogoutIcon />
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>

      <Box component="nav" sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}>
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              width: DRAWER_WIDTH,
              boxSizing: 'border-box',
              pt: 'env(safe-area-inset-top)',
              pb: 'env(safe-area-inset-bottom)',
            },
          }}
        >
          {drawerContent}
        </Drawer>
        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box' },
          }}
          open
        >
          {drawerContent}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          width: { xs: '100%', md: `calc(100% - ${DRAWER_WIDTH}px)` },
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          // Push content below the AppBar's safe-area padding so the spacer
          // Toolbar lines up correctly.
          pt: 'env(safe-area-inset-top)',
          pb: 'env(safe-area-inset-bottom)',
          pl: 'env(safe-area-inset-left)',
          pr: 'env(safe-area-inset-right)',
        }}
      >
        <Toolbar />{/* spacer for fixed AppBar */}
        <Box sx={{ p: { xs: 2, md: 3 }, flex: 1, minWidth: 0 }}>
          {children}
        </Box>
      </Box>
    </Box>
  );
}
